export type HighlightPreviewOptions = {
  allFieldsAsMapped?: boolean;
  hideVariableHighlighting?: boolean;
  variableMappings: Record<string, string>;
};

type PreviewTokenKind = 'signature' | 'variable';

type PreviewToken = {
  index: number;
  inner: string;
  kind: PreviewTokenKind;
  raw: string;
};

const VARIABLE_PATTERN = /\{\{(.*?)\}\}/g;
const SIGNATURE_ANCHOR_PATTERN = /(?<!\[)\[([^[\]\r\n]+)\](?!\])/g;

const escapeHtml = (str: string): string =>
  str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const collectKind = (source: string, pattern: RegExp, kind: PreviewTokenKind): PreviewToken[] => {
  const regex = new RegExp(pattern.source, pattern.flags);
  const tokens: PreviewToken[] = [];
  let match = regex.exec(source);

  while (match != null) {
    if (match[0].length === 0) {
      regex.lastIndex += 1;
      match = regex.exec(source);
      continue;
    }

    tokens.push({
      index: match.index,
      inner: match[1] ?? '',
      kind,
      raw: match[0],
    });
    match = regex.exec(source);
  }

  return tokens;
};

const collectTokens = (source: string): PreviewToken[] => {
  const collected = [
    ...collectKind(source, VARIABLE_PATTERN, 'variable'),
    ...collectKind(source, SIGNATURE_ANCHOR_PATTERN, 'signature'),
  ].sort((left, right) => left.index - right.index || right.raw.length - left.raw.length);

  const tokens: PreviewToken[] = [];
  let cursor = 0;

  for (const token of collected) {
    if (token.index < cursor) continue;
    tokens.push(token);
    cursor = token.index + token.raw.length;
  }

  return tokens;
};

const wrapVariable = (raw: string, inner: string, options: HighlightPreviewOptions): string => {
  if (options.hideVariableHighlighting) {
    return escapeHtml(raw);
  }

  const varName = inner.trim();
  const hasValue = varName !== '' && (options.variableMappings[varName] ?? '') !== '';
  const isMapped = options.allFieldsAsMapped === true || hasValue;
  const bg = isMapped ? 'rgba(3,152,85,0.2)' : 'rgba(247,144,9,0.2)';
  const color = isMapped ? '#16A34A' : '#F59E0B';
  const border = isMapped ? '1px solid #A6F4C5' : '1px solid #FEDF89';

  return `<span style="background:${bg};color:${color};border-radius:3px;border:${border};padding:0 2px">${escapeHtml(raw)}</span>`;
};

const wrapSignature = (raw: string): string =>
  `<span style="background:#9747FF1A;color:#9747FF;border-radius:3px;border:1px solid #D5B5FF;padding:2px 4px">${escapeHtml(raw)}</span>`;

export const buildHighlightedParagraphHtml = (
  fullText: string,
  options: HighlightPreviewOptions,
): null | string => {
  const tokens = collectTokens(fullText);
  if (tokens.length === 0) return null;

  let result = '';
  let lastIndex = 0;

  for (const token of tokens) {
    result += escapeHtml(fullText.slice(lastIndex, token.index));
    result +=
      token.kind === 'variable'
        ? wrapVariable(token.raw, token.inner, options)
        : wrapSignature(token.raw);
    lastIndex = token.index + token.raw.length;
  }

  result += escapeHtml(fullText.slice(lastIndex));
  return result;
};

export const highlightPreviewVariables = (
  container: HTMLElement,
  options: HighlightPreviewOptions,
): void => {
  const paragraphs = container.querySelectorAll<HTMLElement>('p');

  paragraphs.forEach((el) => {
    const html = buildHighlightedParagraphHtml(el.textContent ?? '', options);
    if (html != null) {
      el.innerHTML = html;
    }
  });
};
